import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import HelpIcon from '@material-ui/icons/Help';

import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import MetaControl from 'Components/UI/Meta/MetaControl';
import ErrorList from 'Components/UI/ErrorList';
import LightTooltip from 'Components/UI/LightTooltip';
import SubmitButton from 'Components/UI/SubmitButton';
import useHouseholdResource from 'Hooks/useHouseholdResource';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import RoundButton from 'Components/UI/RoundButton';
import { openModal } from 'Helpers/ModalHelper';
import modalConsts from 'Consts/ModalNames';
import { pickImplementation } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';
import { useMeetingContext } from 'Context/MeetingContext';
import { dispatchMeetingContextRefresh, dispatchMeetingSummaryRefresh } from 'Hooks/useRefreshEvent';
import useResource from 'Hooks/useResource';
import fr from 'facet-rest';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing(0, 0.5, 0.5, 0),
  },
  bnsSelect: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  infoIcon: {
    color: theme.palette.facet.accentGreen,
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
  },
  actionButton: {
    marginTop: theme.spacing(1),
  },
});

const BestNextStep = ({
  className,
  classes,
  householdResource,
}) => {
  const { updateHousehold } = useSimpleHousehold();
  const { agentResource } = useUserContext();
  const { meetingResource } = useMeetingContext();
  const [errors, setErrors] = useState([]);

  const bnsFeatureToggle = meetingResource?.relationship('bestNextSteps');

  const {
    resource: householdResourceCopy,
    setResourceDeprecated: setHouseholdResourceCopy,
  } = useHouseholdResource(householdResource);

  const {
    resource: bnsResource,
    setResourceDeprecated: setBnsResource,
  } = useResource();

  useEffect(() => {
    async function getBNS() {
      if (bnsFeatureToggle) {
        const bnsResponse = await meetingResource.relationship('bestNextSteps').link('related').Get();
        setBnsResource(bnsResponse.resources().head());
      }
    }
    getBNS();
  }, [meetingResource?.id()]);

  const handleSubmit = async () => {
    if (bnsFeatureToggle) {
      try {
        await bnsResource.submit();
        dispatchMeetingContextRefresh();
        dispatchMeetingSummaryRefresh();
      } catch (err) {
        setErrors(err.errors);
        throw err;
      }
    } else {
      try {
        await updateHousehold(householdResourceCopy.export());
        dispatchMeetingSummaryRefresh();
      } catch (err) {
        setErrors(err.errors);
        throw err;
      }
    }
  };

  const res = bnsFeatureToggle ? bnsResource : householdResourceCopy;
  return (
    <div
      className={classNames(classes.root, className)}
    >
      <ErrorList className={classes.errorList} errors={errors} />
      <div className={classes.bnsSelect}>
        { res && <MetaControl
          attrName={bnsFeatureToggle ? 'nextBestNextSteps' : 'bestNextSteps'}
          onChange={(v) => {
            if (bnsFeatureToggle) {
              setBnsResource(bnsResource.attr('nextBestNextSteps', v));
            } else {
              setHouseholdResourceCopy(
                householdResourceCopy.attr('bestNextSteps', v),
              );
            }
          }}
          resource={res}
          fullWidth
          SelectProps={{
            test: 'best-next-step-select',
            renderValue: selected => (
              <div className={classes.chips} test="best-next-step-chips">
                {selected.map(value => (
                  <Chip
                    key={value}
                    label={
                      bnsFeatureToggle
                        ? bnsResource
                          ?.meta()
                          .field('nextBestNextSteps')
                          .option(value)
                          .attr('prompt')
                        : householdResourceCopy
                          .meta()
                          .field('bestNextSteps')
                          .option(value)
                          .attr('prompt')
                    }
                    className={classes.chip}
                  />
                ))}
              </div>
            ),
          }}
        />
        }
        <LightTooltip title="This is to help keep track of the BNS conversations you have with this client.  It doesn't have any other functionality just yet.">
          <HelpIcon fontSize="small" className={classes.infoIcon} />
        </LightTooltip>
      </div>
      <SubmitButton
        className={classes.saveButton}
        color="secondary"
        disableElevation
        onClick={handleSubmit}
        text="Save Best Next Steps"
        rounded
        variant="contained"
      />
      {pickImplementation(agentResource, {
        [fr.relationships.default.name]: (
          <RoundButton
            className={classes.actionButton}
            color="primary"
            disableElevation
            fullWidth
            variant="contained"
            onClick={() => openModal(modalConsts.playbooksModal)}
          >
            View Playbook Tasks
          </RoundButton>
        ),
      })}
    </div>
  );
};

BestNextStep.defaultProps = {
  className: '',
};

BestNextStep.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  householdResource: PropTypes.object.isRequired,
};

export default withStyles(styles)(BestNextStep);
